/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef } from "react";
import { useAuthState } from "../../utilities/contexts/auth-state-context";
import OrganizationService from "../../utilities/services/organization-service";
import { useHistory } from 'react-router-dom';
import { OrganizationStatus } from "../../models/enumerations/organization-status";
import UserService from "../../utilities/services/user-service";
import { Organization } from "../../models/interfaces/organization";
import UserRoles from "../../models/user-roles";
import getFirebase from "../../utilities/firebase";
import { PlanType } from "../../models/enumerations/plan-type";
import { createLogger } from "../../utilities/logger";

const log = createLogger("DeactivatedAccountCheck");

export interface DeactivatedAccountCheckProps {
}

/**
 * A modal that shows up when the user's subscription was cancelled due to a failed payment.
 * 
 * @param props The properties
 * @returns {JSX.Element} The component
 */
const DeactivatedAccountCheck: React.FC<DeactivatedAccountCheckProps> = (props) => {
    const { state, setState } = useAuthState();
    const history = useHistory();
    const { auth } = getFirebase();
    const previousOrganizationRef = useRef<Organization | null>(null);

    // get firebase organization as listener, listen to any changes, detect when a change occurs
    useEffect(() => {
        if (state.organization?.id) {
            const unsubscribe = OrganizationService.getSnapshot(state.organization?.id, (organization: Organization | undefined) => {
                if (!organization) {
                    return;
                }
                if (organization.status === OrganizationStatus.Deactivated && !state.user?.isSuperAdmin) {
                    setState((state) => ({
                        ...state, ...{ organization: organization }
                    }));
                    const orgLogContext = {
                        organizationId: organization.id,
                        organizationName: organization.name,
                        plan: organization.plan?.value,
                        status: OrganizationStatus[Number(organization.status)] ?? organization.status,
                        statusValue: organization.status,
                    };
                    if (state.user?.roles?.includes(UserRoles.ACCOUNT_HOLDER_ID) && organization.plan?.value === PlanType.Trial) {
                        history.replace("/trial-deactivated");
                        log.info("Deactivated org redirect", { ...orgLogContext, destination: "/trial-deactivated" });
                    }
                    else if (state.user?.roles?.includes(UserRoles.ACCOUNT_HOLDER_ID) && (organization.plan?.value === PlanType.Pro || organization.plan?.value === PlanType.Core || organization.plan?.value === PlanType.Business) && organization.showManuallyDeactivatedAccountModal) {
                        history.replace("/plan-deactivated");
                        log.info("Deactivated org redirect", { ...orgLogContext, destination: "/plan-deactivated" });
                    }
                    else if (previousOrganizationRef.current?.plan?.value === PlanType.Trial && (organization.plan?.value === PlanType.Pro || organization.plan?.value === PlanType.Core || organization.plan?.value === PlanType.Business)) {
                        log.info("Deactivated org trial-to-paid transition detected, no redirect", { ...orgLogContext });
                        return;
                    }
                    else if (state.user?.roles?.includes(UserRoles.ACCOUNT_HOLDER_ID) && (organization.plan?.value === PlanType.Core || organization.plan?.value === PlanType.Pro || organization.plan?.value === PlanType.Business) && (organization.showDeactivatedAccountPlanModal || organization.showDeactivatedAccountAddOnModal)) {
                        history.replace("/reactivate-subscription");
                        log.info("Deactivated org redirect", { ...orgLogContext, destination: "/reactivate-subscription" });
                    }
                    else {
                        // logout
                        auth.signOut();
                        log.warn("Deactivated org - signing user out", { ...orgLogContext, destination: "signOut" });
                    }
                }
                previousOrganizationRef.current = organization;
            });
            return () => unsubscribe();
        }
    }, [state.user?.id, state.organization?.status])

    useEffect(() => {
        if (history.location.pathname !== "/login") {
            const fetchUser = async () => {
                if (state.authenticated && state.user?.id) {
                    const user = await UserService.get(state.user.id);

                    if (user && user?.subscription && user?.subscription.didPaymentFail) {
                        setState((state) => ({
                            ...state, ...{ user: user }
                        }));
                        history.replace("/reactivate-user-subscription");
                    }
                }
            }

            if (state.user?.subscription && state.user.subscription.subscriptionId) {
                fetchUser();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (<></>);
};

export default DeactivatedAccountCheck;